/* eslint-disable @typescript-eslint/no-explicit-any */
import { FlatsyApiError, FlatsyValidationError } from '@flatsy/types/Utils/Api'
import { createGlobalErrors } from '@flatsy/utils/Functions/form/Errors'
import axios from 'axios'

export type { FlatsyApiError, FlatsyValidationError } from '@flatsy/types/Utils/Api'

const GLOBAL_ERROR_EXCEPTION = [
  {
    path: '/applications/',
    status: 404,
  },
  {
    path: '/bookings/?size=',
    status: 404,
  },
]

/**
 * @description check if req has to be handled globally or not
 * @param response API response object
 * @returns true if req is in exception else false
 */
const bypassGlobalHandling = (response: any) => {
  if (response && response.config && response.config.url && response.status) {
    const inException = GLOBAL_ERROR_EXCEPTION.find((el) => {
      return response.config.url.includes(el.path) && response.status === el.status
    })

    if (inException) {
      return true
    }
  }

  return false
}

/**
 * RenderError
 * @param error Error object from API
 * @returns Code & message
 */
// eslint-disable-next-line
const renderError = (error: any): FlatsyApiError => {
  let status = 404
  let errors: string[] = []
  let validation_error: FlatsyValidationError | undefined

  if (error && error.response && error.response) {
    if (error.response.status) {
      status = error.response.status
    }

    if (error.response.data && error.response.data.errors) {
      errors = error.response.data.errors
    }

    if (error.response.data && error.response.data.validation_error) {
      validation_error = error.response.data.validation_error
    }
  }
  return {
    code: status,
    errors,
    validation_error,
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleGlobalError = (error: any) => {
  if (error && typeof error === 'object') {
    const snack = document.querySelector('.globalError__snackbar') as HTMLElement | null
    const alerte = document.querySelector(
      '.globalError__alert .MuiAlert-message'
    ) as HTMLElement | null
    if (snack && alerte) {
      snack.style.transition = 'all ease 0.5s'
      snack.style.opacity = '1'
      snack.style.transform = 'translateY(0)'
      snack.style.pointerEvents = 'initial'
      alerte.innerHTML = createGlobalErrors(error.validation_error, error.errors)

      setTimeout(() => {
        snack.style.opacity = '0'
        snack.style.transform = 'translateY(100%)'
        snack.style.pointerEvents = 'none'

        setTimeout(() => {
          alerte.innerHTML = ''
        }, 500)
      }, 2000)
    }
  }
}

/**
 * Catch axios error to refesh token
 */
axios.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    if (error.response.status !== 401 && error.response.status !== 400) {
      if (!bypassGlobalHandling(error.response)) {
        handleGlobalError(error.response.data)
      }
    }
    return Promise.reject(error)
  }
)

/**
 * Client for making request
 * @param endpoint:string endpoint url
 */
type MethodeType =
  | 'POST'
  | 'GET'
  | 'DELETE'
  | 'get'
  | 'delete'
  | 'head'
  | 'HEAD'
  | 'options'
  | 'OPTIONS'
  | 'post'
  | 'put'
  | 'PUT'
  | 'patch'
  | 'PATCH'
  | 'purge'
  | 'PURGE'
  | 'link'
  | 'LINK'
  | 'unlink'
  | 'UNLINK'
  | undefined

export default async function client<T, B = Partial<T>>(
  endpoint: string,
  { body, ...customConfig }: { body?: B; headers?: HeadersInit; method?: MethodeType } = {}
): Promise<T> {
  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  }

  const getCustomConfigMethod = (): MethodeType => {
    // eslint-disable-next-line no-nested-ternary
    return customConfig.method ? customConfig.method : body ? 'POST' : 'GET'
  }

  return axios({
    method: getCustomConfigMethod(),
    url: endpoint,
    data: customConfig.method !== 'GET' ? body : undefined,
    params: customConfig.method === 'GET' ? body : undefined,
    headers: {
      ...headers,
      ...customConfig.headers,
    },
  })
    .then(async (response) => {
      const data = response
      if (response.data) {
        return data.data
      }
    })
    .catch((err) => {
      const errorDetails = renderError(err)
      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({
        errors: errorDetails.errors,
        validation_error: errorDetails.validation_error,
        code: errorDetails.code,
      })
    })
}
